import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useSurah } from '@/hooks/useSurah';
import { useAyahsBySurah } from '@/hooks/useAyah';
import { useSettings } from '@/hooks/useSettings';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useRecordSurahOpen } from '@/hooks/useReadingProgress';
import type { UserSettings } from '@/lib/queries/settings';
import { SurahHeader } from '@/components/reader/SurahHeader';
import { SurahSeo } from '@/components/seo/SurahSeo';
import { AyahLine } from '@/components/reader/AyahLine';
import { ReaderToolbar } from '@/components/reader/ReaderToolbar';
import { AutoScrollControl } from '@/components/reader/AutoScrollControl';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils/cn';
import { useAutoScroll, type AutoScrollSpeed } from '@/hooks/useAutoScroll';
import type { Ayah, TranslationLanguage, TafsirLanguage } from '@/types/domain';
export function SurahReader() {
  const params = useParams<{ number: string; n?: string }>();
  const surahId = Number(params.number);
  const targetAyah = params.n ? Number(params.n) : null;

  const surahQuery = useSurah(Number.isFinite(surahId) ? surahId : null);
  const ayahsQuery = useAyahsBySurah(Number.isFinite(surahId) ? surahId : null);

  const { settings, update } = useSettings();
  const autoScroll = useAutoScroll();

  // Title shows the English name once the surah loads; falls back to the number.
  useDocumentTitle(
    surahQuery.data
      ? `${surahQuery.data.name_en} (Surah ${surahId})`
      : Number.isFinite(surahId)
        ? `Surah ${surahId}`
        : null,
  );

  // Persist reading position (no-op for anonymous users).
  useRecordSurahOpen(
    Number.isFinite(surahId) ? surahId : null,
    targetAyah,
  );

  // Scroll to deep-linked ayah once ayahs are rendered.
  useEffect(() => {
    if (!targetAyah || !ayahsQuery.data?.length) return;
    const el = document.getElementById(`ayah-${surahId}-${targetAyah}`);
    if (el) {
      // Defer to next frame so layout settles first.
      requestAnimationFrame(() => {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }, [targetAyah, surahId, ayahsQuery.data]);

  if (!Number.isFinite(surahId) || surahId < 1 || surahId > 114) {
    return (
      <EmptyState
        title="That surah doesn't exist."
        description="The Quran has 114 surahs, numbered 1 through 114."
        action={
          <Link to="/surah">
            <Button variant="secondary">Back to the surah list</Button>
          </Link>
        }
      />
    );
  }

  if (surahQuery.isError || ayahsQuery.isError) {
    const error = (surahQuery.error ?? ayahsQuery.error) as Error | null;
    return (
      <EmptyState
        tone="error"
        title="We couldn't load this surah."
        description={
          error?.message ??
          'Check your Supabase environment variables and try again.'
        }
        onRetry={() => {
          surahQuery.refetch();
          ayahsQuery.refetch();
        }}
      />
    );
  }

  return (
    <section className="container-page pb-16">
      {/* Per-route SEO meta — only renders once the surah row loads
          (we need name_en, name_translation, etc. to build the tags). */}
      {surahQuery.data && <SurahSeo surah={surahQuery.data} />}

      {surahQuery.isLoading || !surahQuery.data ? (
        <SurahHeaderSkeleton />
      ) : (
        <SurahHeader surah={surahQuery.data} />
      )}

      <SettingsBar
        translationLanguage={settings.translation_language}
        onTranslationLanguageChange={(v) => update({ translation_language: v })}
        showTransliteration={settings.show_transliteration}
        onShowTransliterationChange={(v) => update({ show_transliteration: v })}
        showTafsir={settings.show_tafsir}
        onShowTafsirChange={(v) => update({ show_tafsir: v })}
        tafsirLanguage={settings.tafsir_language}
        onTafsirLanguageChange={(v) => update({ tafsir_language: v })}
        autoScrollSpeed={autoScroll.speed}
        onAutoScrollSpeedChange={autoScroll.setSpeed}
      />

      <div className="mt-8">
        {renderAyahs({
          isLoading: ayahsQuery.isLoading,
          ayahs: ayahsQuery.data ?? [],
          settings,
        })}
      </div>
    </section>
  );
}

function renderAyahs({
  isLoading,
  ayahs,
  settings,
}: {
  isLoading: boolean;
  ayahs: Ayah[];
  settings: UserSettings;
}) {
  if (isLoading) return <AyahListSkeleton />;
  if (ayahs.length === 0) {
    return (
      <EmptyState
        title="No ayahs were returned for this surah."
        description="The expected count is on the surah header above. If this looks wrong, check Supabase access policies for quran_core.ayahs."
      />
    );
  }
  return ayahs.map((ayah, idx) => {
    const previous = idx > 0 ? ayahs[idx - 1] : undefined;
    const showJuzMarker =
      !!ayah.juz && (!previous || previous.juz !== ayah.juz);
    return (
      <AyahLine
        key={ayah.id}
        ayah={ayah}
        translationLanguage={settings.translation_language}
        showTransliteration={settings.show_transliteration}
        showTafsir={settings.show_tafsir}
        tafsirLanguage={settings.tafsir_language}
        showJuzMarker={showJuzMarker}
      />
    );
  });
}

function SurahHeaderSkeleton() {
  return (
    <header className="text-center pt-12 pb-10">
      <div className="flex items-center justify-center gap-2 mb-5">
        <Skeleton className="h-5 w-16" />
        <Skeleton className="h-5 w-16" />
      </div>
      <Skeleton className="h-12 w-56 mx-auto mb-3" />
      <Skeleton className="h-6 w-40 mx-auto" />
      <div className="mx-auto mt-10 h-px w-24 bg-accent/30" />
    </header>
  );
}

function AyahListSkeleton() {
  return (
    <ul>
      {Array.from({ length: 5 }).map((_, i) => (
        <li key={i} className="py-10 border-b border-ink-200/50 dark:border-ink-800/50">
          <Skeleton className="h-4 w-12 mb-4" />
          <Skeleton className="h-8 w-full mb-2" />
          <Skeleton className="h-8 w-3/4 mb-6" />
          <Skeleton className="h-4 w-full mb-1" />
          <Skeleton className="h-4 w-5/6" />
        </li>
      ))}
    </ul>
  );
}

type SettingsBarProps = {
  translationLanguage: TranslationLanguage;
  onTranslationLanguageChange: (v: TranslationLanguage) => void;
  showTransliteration: boolean;
  onShowTransliterationChange: (v: boolean) => void;
  showTafsir: boolean;
  onShowTafsirChange: (v: boolean) => void;
  tafsirLanguage: TafsirLanguage;
  onTafsirLanguageChange: (v: TafsirLanguage) => void;
  // Auto-scroll lives on its own row below the pill cluster so it
  // doesn't crowd the EN/UR/BOTH/TR row.
  autoScrollSpeed: AutoScrollSpeed;
  onAutoScrollSpeedChange: (next: AutoScrollSpeed) => void;
};

function SettingsBar({
  translationLanguage,
  onTranslationLanguageChange,
  showTransliteration,
  onShowTransliterationChange,
  showTafsir,
  onShowTafsirChange,
  tafsirLanguage,
  onTafsirLanguageChange,
  autoScrollSpeed,
  onAutoScrollSpeedChange,
}: SettingsBarProps) {
  return (
    <div
      role="toolbar"
      aria-label="Reader settings"
      className={cn(
        'sticky top-14 z-20',
        'py-3 -mx-5 sm:-mx-8 px-5 sm:px-8',
        // Glass backdrop so the pill cluster hides the ayah text behind
        // it as the user scrolls past, instead of stacking pills on top
        // of Arabic. Matches the Header's blur + tint idiom.
        'bg-night-900/85 backdrop-blur-md',
        'border-b border-night-500/15',
      )}
    >
      {/* Pill cluster — allowed to overflow horizontally on narrow
          viewports. The Auto scroll pill is rendered separately on its
          own centered row below this cluster (see next block). */}
      <div className="overflow-x-auto scrollbar-thin -mx-5 sm:mx-0 px-5 sm:px-0 min-w-0">
        <div className="flex justify-center min-w-min">
          <ReaderToolbar
            translationLanguage={translationLanguage}
            onTranslationLanguageChange={onTranslationLanguageChange}
            showTransliteration={showTransliteration}
            onShowTransliterationChange={onShowTransliterationChange}
            showTafsirControls
            showTafsir={showTafsir}
            onShowTafsirChange={onShowTafsirChange}
            tafsirLanguage={tafsirLanguage}
            onTafsirLanguageChange={onTafsirLanguageChange}
          />
        </div>
      </div>

      {/* Auto scroll — own centered row, never crowds EN/UR/BOTH/TR.
          Dropdown pops below the pill via the component's own absolute
          positioning. */}
      <div className="mt-3 flex justify-center">
        <AutoScrollControl
          speed={autoScrollSpeed}
          onSpeedChange={onAutoScrollSpeedChange}
        />
      </div>
    </div>
  );
}
